import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faEye, faPaperPlane, faTrash, faWrench } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import DeleteModal from 'components/modals/DeleteModal';
import Dropdown from 'components/forms/basefields/Dropdown';
import SwitchButton from 'components/buttons/SwitchButton';
import TextareaInput from 'components/forms/basefields/TextareaInput';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import KnowledgeBaseEditor from 'components/forms/fields/KnowledgeBaseEditor';
import NewsletterSendModal from './NewsletterSendModal';
import NewsletterSendNotice, { useNewsletterSendStatus } from './NewsletterSendNotice';
import { Newsletter, NewsletterAudience, NEWSLETTER_AUDIENCES_PATH, NEWSLETTER_PATH } from './NewsletterTypes';
import '../../../style/scss/forms.scss';
import '../../../style/scss/detailpage.scss';
import '../../../style/scss/knowledge-base.scss';
import '../../../style/scss/newsletters.scss';

// The editable fields of a newsletter
interface NewsletterFormState {
    subject: string;
    preheader: string;
    body: string;
    layout: number | null;
    sending_address: number | null;
    from_name: string;
    audience: number | null;
    is_release: boolean;
}

// A selectable layout or sending address
interface IdOption {
    id: number;
    name: string;
}

// The editor page of a newsletter, the fields in cards on the left and the editor on the right like the email template editor
const NewsletterEditorPage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();
    const { initializeModal } = useModal();
    const { setFloatingAlert, setUnsavedChanges } = useGlobalContext();
    const { environmentType } = useAuthContext();
    const viewKey = useMemo(() => uuidv4(), []);
    const isNew = !id;

    // Only the admin environment marks a newsletter as a platform update
    const canMarkRelease = environmentType === 'admin';

    // The current newsletter id, set after the first save of a new newsletter
    const [newsletterId, setNewsletterId] = useState<number | null>(id ? Number(id) : null);
    const [form, setForm] = useState<NewsletterFormState>({
        subject: '', preheader: '', body: '', layout: null, sending_address: null, from_name: '', audience: null, is_release: false,
    });
    const [audienceCount, setAudienceCount] = useState<number | null>(null);
    const [audienceCountError, setAudienceCountError] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const skipAutosaveRef = useRef(true);
    const savedFormRef = useRef<NewsletterFormState | null>(null);
    const isDirtyRef = useRef(false);

    // Keep the latest context setters in refs since they change identity on every global render
    const setUnsavedChangesRef = useRef(setUnsavedChanges);
    const setFloatingAlertRef = useRef(setFloatingAlert);
    setUnsavedChangesRef.current = setUnsavedChanges;
    setFloatingAlertRef.current = setFloatingAlert;

    // Marks the form as dirty or clean and only touches the global state on a change
    const markDirty = useCallback((dirty: boolean) => {
        setIsDirty(dirty);
        if (isDirtyRef.current === dirty) return;
        isDirtyRef.current = dirty;
        setUnsavedChangesRef.current(viewKey, dirty);
    }, [viewKey]);

    // Fetch the existing newsletter
    const { response: newsletter } = useFetchData({ apiUrl: isNew ? '' : `get_newsletter/${id}` });

    // Fetch the layouts, sending addresses and audiences of the environment and the send status
    const activeParams = useMemo(() => ({ deleted: false }), []);
    const { response: layoutResponse } = useFetchData({ apiUrl: 'get_email_layout_list', params: activeParams });
    const { response: addressResponse } = useFetchData({ apiUrl: 'get_email_sending_address_list', params: activeParams });
    const { response: audienceResponse } = useFetchData({ apiUrl: 'get_newsletter_audience_list' });
    const sendStatus = useNewsletterSendStatus();
    const audiences: NewsletterAudience[] = useMemo(() => audienceResponse?.results ?? [], [audienceResponse]);

    // Count the contacts of the chosen audience, every contact with an email address without an audience
    useEffect(() => {
        let cancelled = false;
        setAudienceCount(null);
        setAudienceCountError(null);
        const data = form.audience ? { audience: form.audience } : { audience_conditions: [] };
        saveData({ apiUrl: 'post_newsletter_audience_count', method: 'post', data })
            .then(response => { if (!cancelled) setAudienceCount(response?.data?.count ?? null); })
            .catch(error => { if (!cancelled) setAudienceCountError(error?.response?.data?.general ?? 'newsletters.audience.count_failed'); });
        return () => { cancelled = true; };
    }, [form.audience]);

    // Fill the form with the fetched newsletter, a newsletter which is no concept opens its report
    useEffect(() => {
        if (!newsletter) return;
        const fetched = newsletter as Newsletter;
        if (fetched.status !== 'concept') {
            history.replace(`${NEWSLETTER_PATH}/${fetched.id}/report`);
            return;
        }
        setForm({
            subject: fetched.subject ?? '',
            preheader: fetched.preheader ?? '',
            body: fetched.body ?? '',
            layout: fetched.layout,
            sending_address: fetched.sending_address,
            from_name: fetched.from_name ?? '',
            audience: fetched.audience,
            is_release: fetched.is_release ?? false,
        });
        skipAutosaveRef.current = true;
    }, [newsletter]);

    // Updates a field of the form and marks the form as dirty
    const updateField = <K extends keyof NewsletterFormState>(field: K, value: NewsletterFormState[K]) => {
        setForm(current => ({ ...current, [field]: value }));
        markDirty(true);
    };

    // Saves the newsletter, creates it on the first save, returns the saved id
    const saveNewsletter = useCallback(async (showAlert: boolean): Promise<number | null> => {
        if (isSaving) return newsletterId;
        setIsSaving(true);
        setErrors({});

        try {
            // Post a new newsletter or patch the existing one, remember which state is saved
            savedFormRef.current = form;
            const data = { ...form, from_name: form.from_name || null };
            const response = newsletterId
                ? await saveData({ apiUrl: `patch_newsletter/${newsletterId}`, method: 'patch', data })
                : await saveData({ apiUrl: 'post_newsletter', method: 'post', data });

            // Store the saved state
            const saved = response?.data as Newsletter;
            setLastSaved(new Date());

            // Only clear the dirty state when nothing changed during the save
            setForm(current => {
                markDirty(current !== savedFormRef.current);
                return current;
            });
            if (showAlert) setFloatingAlertRef.current({ type: 'success', message: 'newsletters.editor.saved_message' });

            // Replace the url of a new newsletter with the url of the saved newsletter
            if (!newsletterId && saved?.id) {
                setNewsletterId(saved.id);
                history.replace(`${NEWSLETTER_PATH}/${saved.id}`);
            }
            return saved?.id ?? newsletterId;
        } catch (error: any) {
            // Show the field errors or a general alert
            const responseErrors = error?.response?.data;
            if (responseErrors && typeof responseErrors === 'object') {
                const fieldErrors: Record<string, string> = {};
                Object.entries(responseErrors).forEach(([field, message]) => { fieldErrors[field] = Array.isArray(message) ? String(message[0]) : String(message); });
                setErrors(fieldErrors);
            }
            setFloatingAlertRef.current({ type: 'danger', message: 'newsletters.general.save_failed_message' });
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [newsletterId, form, history, isSaving, markDirty]);

    // Autosave an existing newsletter a moment after the last change
    useEffect(() => {
        if (skipAutosaveRef.current) { skipAutosaveRef.current = false; return; }
        if (!isDirty || !newsletterId) return;
        const timer = setTimeout(() => { saveNewsletter(false); }, 2000);
        return () => clearTimeout(timer);
    }, [form, isDirty, newsletterId, saveNewsletter]);

    // Clear the unsaved changes state when leaving the page
    useEffect(() => () => setUnsavedChangesRef.current(viewKey, false), [viewKey]);

    // Opens the delete confirmation of the newsletter
    const handleDelete = () => {
        if (!newsletterId) { history.push(NEWSLETTER_PATH); return; }
        initializeModal(
            <DeleteModal onConfirmDelete={async () => {
                try {
                    await saveData({ apiUrl: `patch_newsletter/${newsletterId}`, method: 'patch', data: { deleted: true } });
                    markDirty(false);
                    history.push(NEWSLETTER_PATH);
                } catch (error) {
                    setFloatingAlert({ type: 'danger', message: 'newsletters.general.save_failed_message' });
                }
            }} />,
            { modalSize: 'extra-small' }
        );
    };

    // Saves the newsletter and opens the modal with the preview and the recipients, to look or to send
    const openNewsletterModal = async (mode: 'preview' | 'send') => {
        if (mode === 'send' && (!form.subject.trim() || !form.body.trim())) {
            setFloatingAlert({ type: 'danger', message: 'newsletters.validation.subject_and_body_required' });
            return;
        }
        const savedId = isDirty || !newsletterId ? await saveNewsletter(false) : newsletterId;
        if (!savedId) return;
        initializeModal(
            <NewsletterSendModal
                newsletterId={savedId}
                subject={form.subject}
                audience={form.audience}
                sendStatus={sendStatus}
                mode={mode}
                onSent={() => { markDirty(false); history.push(`${NEWSLETTER_PATH}/${savedId}/report`); }} />,
            { title: t(mode === 'send' ? 'newsletters.send.title' : 'newsletters.send.preview_modal_title'), modalSize: 'large' }
        );
    };

    // The layout options with the default layout first
    const layoutOptions: IdOption[] = useMemo(() => (
        ((layoutResponse?.results ?? []) as any[]).map(layout => ({ id: layout.id, name: layout.name || t('newsletters.editor.layout_unnamed', { id: layout.id }) }))
    ), [layoutResponse, t]);

    // The sending address options
    const addressOptions: IdOption[] = useMemo(() => (
        ((addressResponse?.results ?? []) as any[]).map(address => ({ id: address.id, name: address.email }))
    ), [addressResponse]);

    // The audience options, the chosen audience stays selectable when it was deleted so the user sees it
    const audienceOptions: IdOption[] = useMemo(() => audiences.map(audience => ({ id: audience.id, name: audience.name })), [audiences]);
    const audienceDeleted = Boolean(form.audience && audienceResponse && !audiences.some(audience => audience.id === form.audience));

    // Describes the count line below the audience choice
    const describeAudienceCount = (): string => {
        if (audienceDeleted) return t('newsletters.validation.audience_deleted');
        if (audienceCountError) return t(audienceCountError);
        if (audienceCount === null) return t('newsletters.audience.counting');
        if (audienceCount === 0) return t('newsletters.audience.count_zero');
        return t('newsletters.editor.audience_count', { count: audienceCount });
    };

    // The extra data of the image uploads, memoized so the editor does not rebind its handlers
    const uploadData = useMemo(() => ({ newsletter: newsletterId ?? null }), [newsletterId]);

    // Describes the save state shown next to the footer buttons
    const describeSaveState = (): string | null => {
        if (isSaving) return t('newsletters.editor.saving');
        if (isDirty) return t('newsletters.editor.unsaved');
        if (lastSaved) return t('newsletters.editor.saved_at', { time: lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) });
        return null;
    };

    return (
        <div className='detailpage newsletter-editor-page container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span className='back-button' onClick={() => history.push(NEWSLETTER_PATH)}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>
                        {form.subject.trim() || t(isNew ? 'newsletters.editor.page_title_new' : 'newsletters.general.untitled')}
                    </h2>
                </div>
                <div className='header-buttons'>
                    {newsletterId && (
                        <SecondaryButton label='general.delete' size='small' icon={<FontAwesomeIcon icon={faTrash} />} onClick={handleDelete} />
                    )}
                    <SecondaryButton label='newsletters.editor.preview_button' size='small' icon={<FontAwesomeIcon icon={faEye} />} onClick={() => openNewsletterModal('preview')} />
                    <PrimaryButton label='newsletters.editor.send_button' size='small' icon={<FontAwesomeIcon icon={faPaperPlane} />} onClick={() => openNewsletterModal('send')} />
                </div>
            </div>

            <NewsletterSendNotice status={sendStatus} />

            <div className='detailpage-content'>
                <div>
                    {/* The subject and the preheader of the message */}
                    <div className='card'>
                        <h6 className='newsletter-card-header'>{t('newsletters.editor.details_title')}</h6>
                        <div className='field-group'>
                            <div className='edit-mode'>
                                <label>{t('newsletters.editor.subject_label')}</label>
                                <input
                                    type='text'
                                    id='subject'
                                    name='subject'
                                    className={`text ${errors.subject ? 'is-invalid' : ''}`}
                                    placeholder={t('newsletters.editor.subject_placeholder')}
                                    value={form.subject}
                                    onChange={(event) => updateField('subject', event.target.value)} />
                                {errors.subject && <div className='error-message'>{errors.subject}</div>}
                            </div>
                            <div className='edit-mode'>
                                <label>{t('newsletters.editor.preheader_label')}</label>
                                <TextareaInput
                                    id='preheader'
                                    name='preheader'
                                    rows={3}
                                    placeholder='newsletters.editor.preheader_placeholder'
                                    value={form.preheader}
                                    onChange={(value) => updateField('preheader', typeof value === 'string' ? value : value.target.value)} />
                                <p className='helper-text'>{t('newsletters.editor.preheader_helper_text')}</p>
                            </div>
                            {canMarkRelease && (
                                <div className='edit-mode'>
                                    <div className='newsletter-switch-row'>
                                        <label>{t('newsletters.editor.is_release_label')}</label>
                                        <SwitchButton
                                            value={form.is_release}
                                            onToggle={(isOn) => updateField('is_release', isOn)} />
                                    </div>
                                    {errors.is_release && <div className='error-message'>{t(errors.is_release)}</div>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* The audience of the newsletter */}
                    <div className='card'>
                        <h6 className='newsletter-card-header'>{t('newsletters.audience.title')}</h6>
                        <div className='field-group'>
                            <div className='edit-mode'>
                                <div className='label-link-wrapper'>
                                    <label>{t('newsletters.editor.audience_label')}</label>
                                    <span className='text-link' onClick={() => history.push(NEWSLETTER_AUDIENCES_PATH)}>
                                        {t('newsletters.editor.audience_manage')}
                                        <FontAwesomeIcon icon={faWrench} className='text-link-icon' />
                                    </span>
                                </div>
                                <Dropdown<IdOption>
                                    name='audience' options={audienceOptions} optionFormat='name' showSearch={audienceOptions.length > 8} allowNoneOption={true}
                                    noneOptionLabel='newsletters.editor.audience_all'
                                    errorMessage={errors.audience ? t(errors.audience) : undefined}
                                    selectedOption={audienceOptions.find(option => option.id === form.audience) ?? null}
                                    selectionFormat={(option) => option.name}
                                    onChange={(value) => updateField('audience', value === 'none' ? null : Number(value))} />
                                <p className={`helper-text ${audienceDeleted || audienceCountError ? 'newsletter-helper-error' : ''} ${audienceCount === 0 ? 'newsletter-helper-warning' : ''}`}>
                                    {describeAudienceCount()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* The sender and the layout of the message */}
                    <div className='card'>
                        <h6 className='newsletter-card-header'>{t('newsletters.editor.sender_title')}</h6>
                        <div className='field-group'>
                            <div className='edit-mode'>
                                <label>{t('newsletters.editor.sending_address_label')}</label>
                                <Dropdown<IdOption>
                                    name='sending_address' options={addressOptions} optionFormat='name' showSearch={addressOptions.length > 8} allowNoneOption={true}
                                    noneOptionLabel='newsletters.editor.sending_address_default'
                                    errorMessage={errors.sending_address ? t(errors.sending_address) : undefined}
                                    selectedOption={addressOptions.find(option => option.id === form.sending_address) ?? null}
                                    selectionFormat={(option) => option.name}
                                    onChange={(value) => updateField('sending_address', value === 'none' ? null : Number(value))} />
                            </div>
                            <div className='edit-mode'>
                                <label>{t('newsletters.editor.from_name_label')}</label>
                                <input
                                    type='text'
                                    id='from_name'
                                    name='from_name'
                                    className='text'
                                    placeholder={t('newsletters.editor.from_name_placeholder')}
                                    value={form.from_name}
                                    onChange={(event) => updateField('from_name', event.target.value)} />
                            </div>
                            <div className='edit-mode'>
                                <label>{t('newsletters.editor.layout_label')}</label>
                                <Dropdown<IdOption>
                                    name='layout' options={layoutOptions} optionFormat='name' showSearch={false} allowNoneOption={true}
                                    noneOptionLabel='newsletters.editor.layout_default'
                                    selectedOption={layoutOptions.find(option => option.id === form.layout) ?? null}
                                    selectionFormat={(option) => option.name}
                                    onChange={(value) => updateField('layout', value === 'none' ? null : Number(value))} />
                            </div>
                        </div>
                    </div>

                </div>

                <div>
                    {/* The body of the newsletter */}
                    <div className='card newsletter-editor-card'>
                        <h6 className='newsletter-card-header'>{t('newsletters.editor.body_title')}</h6>
                        <KnowledgeBaseEditor
                            value={form.body}
                            onChange={(value) => updateField('body', value)}
                            uploadApiUrl='post_newsletter_image'
                            uploadData={uploadData}
                            placeholder='newsletters.editor.body_placeholder'
                            onUploadError={() => setFloatingAlert({ type: 'danger', message: 'knowledge_base.editor.image_upload_failed' })} />
                    </div>
                </div>
            </div>

            <div className='footer-fixed'>
                <div className='container-filled'>
                    <div className='newsletter-footer-left'>
                        <SecondaryButton
                            onClick={() => history.push(NEWSLETTER_PATH)}
                            label='general.cancel'
                            size='small' />
                        {describeSaveState() && <span className='newsletter-save-state'>{describeSaveState()}</span>}
                    </div>
                    <PrimaryButton
                        onClick={() => saveNewsletter(true)}
                        label='general.save'
                        size='small'
                        loading={isSaving} />
                </div>
            </div>
        </div>
    );
};

export default NewsletterEditorPage;
